import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config.js';
import authService from '../../services/AuthService.js';
import {
  Building2, CreditCard, Smartphone, Receipt, Scale, Bell, 
  RotateCcw, ShieldCheck, Plus, Save, Trash2, Edit2, Star, 
  CheckCircle2, XCircle, Loader, RefreshCw, QrCode, Download,
  AlertTriangle, ChevronRight
} from 'lucide-react';

const PURPOSES = ['FEE', 'HOSTEL', 'TRANSPORT', 'EXAM', 'SCHOLARSHIP', 'SALARY', 'OTHER'];
const GATEWAY_LIST = ['RAZORPAY', 'PHONEPE', 'PAYTM', 'CASHFREE', 'STRIPE', 'CCAVENUE', 'HDFC_SMARTHUB', 'BILLDESK'];

const SUB_TABS = [
  { key: 'bank', label: 'Bank Accounts', icon: Building2 },
  { key: 'gateways', label: 'Payment Gateways', icon: CreditCard },
  { key: 'upi', label: 'UPI & QR', icon: Smartphone },
  { key: 'receipt', label: 'Receipt Numbering', icon: Receipt },
  { key: 'rules', label: 'Fee Collection Rules', icon: Scale },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'refund', label: 'Refund Policies', icon: RotateCcw },
  { key: 'security', label: 'Security', icon: ShieldCheck },
];

// ═══════════════ STYLES ═══════════════
const cardStyle = { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' };
const sectionTitle = (text, icon) => (
  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
    {icon} {text}
  </h3>
);
const labelStyle = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', transition: 'border 0.2s' };
const btnPrimary = { padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' };
const btnOutline = { padding: '9px 20px', background: '#fff', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' };
const toggleStyle = (on) => ({
  width: '44px', height: '24px', borderRadius: '12px', background: on ? '#2563eb' : '#cbd5e1',
  position: 'relative', cursor: 'pointer', transition: 'background 0.2s', border: 'none', padding: 0, flexShrink: 0
});
const toggleKnob = (on) => ({
  width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px',
  left: on ? '22px' : '4px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
});
const gridForm = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' };
const toggleRow = (label, value, onChange, desc) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
    <div>
      <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{label}</div>
      {desc && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{desc}</div>}
    </div>
    <button type="button" style={toggleStyle(value)} onClick={() => onChange(!value)}>
      <span style={toggleKnob(value)} />
    </button>
  </div>
);

const PaymentBankSettings = ({ orgData, showNotification }) => {
  const [activeSection, setActiveSection] = useState('bank');
  const [settings, setSettings] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Bank Account Modal
  const [showBankModal, setShowBankModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [bankForm, setBankForm] = useState({ accountName: '', purpose: 'FEE', accountHolderName: '', bankName: '', branchName: '', accountNumber: '', ifscCode: '', upiId: '', isDefault: false, isActive: true });

  // Gateway expand
  const [expandedGateway, setExpandedGateway] = useState(null);

  const apiCall = useCallback(async (url, options = {}) => {
    const token = authService.getToken();
    const res = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers }
    });
    return res.json();
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, accountsRes, gatewaysRes] = await Promise.all([
        apiCall('/api/payment/settings'),
        apiCall('/api/payment/bank-accounts'),
        apiCall('/api/payment/gateways'),
      ]);
      if (settingsRes && settingsRes.success && settingsRes.settings) {
        setSettings(settingsRes.settings);
      } else {
        setSettings({
          receiptPrefix: 'FEE',
          receiptPadding: 6,
          receiptAutoNumber: true,
          lateFeePerDay: 0,
          graceDays: 5,
          maximumFine: 1000,
          allowInstallments: true,
          allowPartialPayment: true,
          acceptCash: true,
          acceptCheque: true,
          acceptDD: true,
          acceptBankTransfer: true
        });
      }
      if (accountsRes && accountsRes.success) setBankAccounts(accountsRes.accounts || []);
      if (gatewaysRes && gatewaysRes.success) setGateways(gatewaysRes.gateways || []);
    } catch (err) {
      console.error('Failed to load payment config:', err);
      setSettings({
        receiptPrefix: 'FEE',
        receiptPadding: 6,
        receiptAutoNumber: true,
        lateFeePerDay: 0,
        graceDays: 5,
        maximumFine: 1000,
        allowInstallments: true,
        allowPartialPayment: true,
        acceptCash: true,
        acceptCheque: true,
        acceptDD: true,
        acceptBankTransfer: true
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = async (section) => {
    setSaving(true);
    try {
      const res = await apiCall('/api/payment/settings', { method: 'POST', body: JSON.stringify(settings) });
      if (res.success) showNotification?.('success', 'Saved', `${section} settings saved successfully`);
      else throw new Error(res.error || 'Save failed');
    } catch (err) {
      showNotification?.('error', 'Error', err.message);
    } finally { setSaving(false); }
  };

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  // Bank Account CRUD
  const openAddBank = () => { setEditingAccount(null); setBankForm({ accountName: '', purpose: 'FEE', accountHolderName: '', bankName: '', branchName: '', accountNumber: '', ifscCode: '', upiId: '', isDefault: false, isActive: true }); setShowBankModal(true); };
  const openEditBank = (acc) => { setEditingAccount(acc); setBankForm({ ...acc }); setShowBankModal(true); };
  const saveBankAccount = async () => {
    setSaving(true);
    try {
      const url = editingAccount ? `/api/payment/bank-accounts/${editingAccount.id}` : '/api/payment/bank-accounts';
      const method = editingAccount ? 'PUT' : 'POST';
      const res = await apiCall(url, { method, body: JSON.stringify(bankForm) });
      if (res.success) { showNotification?.('success', 'Saved', editingAccount ? 'Bank account updated' : 'Bank account added'); setShowBankModal(false); loadAll(); }
      else throw new Error(res.error || 'Failed');
    } catch (err) { showNotification?.('error', 'Error', err.message); }
    finally { setSaving(false); }
  };
  const deleteBank = async (id) => {
    if (!window.confirm('Delete this bank account?')) return;
    const res = await apiCall(`/api/payment/bank-accounts/${id}`, { method: 'DELETE' });
    if (res.success) { showNotification?.('success', 'Deleted', 'Bank account removed'); loadAll(); }
  };

  // Gateway save
  const saveGateway = async (gw) => {
    setSaving(true);
    try {
      const res = await apiCall('/api/payment/gateways', { method: 'POST', body: JSON.stringify(gw) });
      if (res.success) { showNotification?.('success', 'Saved', `${gw.gatewayName} configuration saved`); loadAll(); }
    } catch (err) { showNotification?.('error', 'Error', err.message); }
    finally { setSaving(false); }
  };

  if (loading || !settings) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', padding: '60px' }}>
        <Loader size={28} color="#2563eb" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', marginTop: '12px' }}>Loading payment configuration...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '0', minHeight: '600px' }}>
      {/* Left Sub-Navigation */}
      <div style={{ width: '220px', background: '#f8fafc', borderRadius: '12px 0 0 12px', border: '1px solid #e2e8f0', borderRight: 'none', flexShrink: 0 }}>
        <div style={{ padding: '16px 14px 8px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Configuration</div>
        {SUB_TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeSection === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveSection(tab.key)}
              style={{ width: '100%', padding: '10px 14px', border: 'none', background: isActive ? '#ffffff' : 'transparent', color: isActive ? '#1e40af' : '#475569', fontSize: '13px', fontWeight: isActive ? '600' : '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', borderRight: isActive ? '3px solid #2563eb' : '3px solid transparent', transition: 'all 0.15s' }}>
              <Icon size={15} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Right Content */}
      <div style={{ flex: 1, background: '#fff', borderRadius: '0 12px 12px 0', border: '1px solid #e2e8f0', padding: '24px', overflowY: 'auto' }}>

        {/* ═══ BANK ACCOUNTS ═══ */}
        {activeSection === 'bank' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              {sectionTitle('Institution Bank Accounts', <Building2 size={20} color="#2563eb" />)}
              <button style={btnPrimary} onClick={openAddBank}><Plus size={14} /> Add Bank Account</button>
            </div>
            {bankAccounts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                <Building2 size={40} color="#cbd5e1" />
                <p style={{ marginTop: '12px' }}>No bank accounts configured. Add your first account.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {bankAccounts.map(acc => (
                  <div key={acc.id} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: acc.isDefault ? '#eff6ff' : '#fff' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>{acc.accountName}</span>
                        {acc.isDefault && <span style={{ fontSize: '10px', background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>DEFAULT</span>}
                        <span style={{ fontSize: '10px', background: acc.isActive ? '#dcfce7' : '#fee2e2', color: acc.isActive ? '#16a34a' : '#dc2626', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>{acc.isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                        <span style={{ fontSize: '10px', background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>{acc.purpose}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {acc.bankName} {acc.branchName ? `• ${acc.branchName}` : ''} {acc.accountNumber ? `• A/C: ****${acc.accountNumber.slice(-4)}` : ''} {acc.ifscCode ? `• IFSC: ${acc.ifscCode}` : ''}
                      </div>
                      {acc.upiId && <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '2px' }}>UPI: {acc.upiId}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button style={{ ...btnOutline, padding: '6px 10px' }} onClick={() => openEditBank(acc)}><Edit2 size={13} /></button>
                      <button style={{ ...btnOutline, padding: '6px 10px', color: '#dc2626', borderColor: '#fecaca' }} onClick={() => deleteBank(acc.id)}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ PAYMENT GATEWAYS ═══ */}
        {activeSection === 'gateways' && (
          <div>
            {sectionTitle('Payment Gateway Configuration', <CreditCard size={20} color="#2563eb" />)}
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>Configure online payment gateways. Each gateway can have its own credentials and environment.</p>
            <div style={{ display: 'grid', gap: '10px' }}>
              {GATEWAY_LIST.map(name => {
                const existing = gateways.find(g => g.gatewayName === name);
                const isExpanded = expandedGateway === name;
                const gwData = existing || { gatewayName: name, enabled: false, merchantId: '', keyId: '', secretKey: '', webhookSecret: '', environment: 'SANDBOX' };
                return (
                  <div key={name} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: isExpanded ? '#f8fafc' : '#fff' }}
                         onClick={() => setExpandedGateway(isExpanded ? null : name)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{name.replace(/_/g, ' ')}</span>
                        {gwData.enabled && <span style={{ fontSize: '10px', background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>ENABLED</span>}
                        {gwData.environment === 'PRODUCTION' && gwData.enabled && <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>LIVE</span>}
                      </div>
                      <ChevronRight size={16} style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: '#94a3b8' }} />
                    </div>
                    {isExpanded && (
                      <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
                        <GatewayForm gateway={gwData} onSave={saveGateway} saving={saving} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ UPI & QR ═══ */}
        {activeSection === 'upi' && (
          <div>
            {sectionTitle('UPI & QR Code Settings', <Smartphone size={20} color="#2563eb" />)}
            
            {toggleRow('Enable UPI Payments', settings.enableUpi !== false, v => updateSetting('enableUpi', v), 'Allow parents and students to make fee payments via UPI deep-links')}

            <div style={{ ...gridForm, opacity: settings.enableUpi !== false ? 1 : 0.5, pointerEvents: settings.enableUpi !== false ? 'auto' : 'none' }}>
              <div>
                <label style={labelStyle}>School UPI ID (VPA Address) *</label>
                <input style={inputStyle} value={settings.upiId || ''} onChange={e => updateSetting('upiId', e.target.value)} placeholder="9980190691@YBL" />
              </div>
              <div>
                <label style={labelStyle}>UPI Display Name (Merchant Name) *</label>
                <input style={inputStyle} value={settings.merchantName || ''} onChange={e => updateSetting('merchantName', e.target.value)} placeholder="Institution Merchant Name" />
              </div>
              <div>
                <label style={labelStyle}>Merchant Code (MCC - Optional)</label>
                <input style={inputStyle} value={settings.merchantCode || ''} onChange={e => updateSetting('merchantCode', e.target.value)} placeholder="8211" />
              </div>
            </div>

            {/* Payment Options Checkboxes */}
            <div style={{ marginTop: '20px', padding: '16px 20px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <label style={{ ...labelStyle, fontSize: '13px', fontWeight: '700', marginBottom: '12px', display: 'block' }}>Payment Options (Enabled Apps for Parents)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                  <input type="checkbox" checked={settings.enableGpay !== false} onChange={e => updateSetting('enableGpay', e.target.checked)} />
                  🟣 Google Pay
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                  <input type="checkbox" checked={settings.enablePhonepe !== false} onChange={e => updateSetting('enablePhonepe', e.target.checked)} />
                  🔵 PhonePe
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                  <input type="checkbox" checked={settings.enablePaytm !== false} onChange={e => updateSetting('enablePaytm', e.target.checked)} />
                  🔵 Paytm
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                  <input type="checkbox" checked={settings.enableOtherUpi !== false} onChange={e => updateSetting('enableOtherUpi', e.target.checked)} />
                  📱 Other UPI Apps
                </label>
              </div>
            </div>

            {/* Payment Verification Method */}
            <div style={{ marginTop: '20px', padding: '16px 20px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <label style={{ ...labelStyle, fontSize: '13px', fontWeight: '700', marginBottom: '12px', display: 'block' }}>Payment Verification Mode</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="verificationMode" 
                    checked={(settings.verificationMode || 'MANUAL_UTR') === 'MANUAL_UTR'} 
                    onChange={() => updateSetting('verificationMode', 'MANUAL_UTR')} 
                    style={{ marginTop: '3px' }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>● Manual UTR Verification (Recommended for UPI ID/VPA)</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Parent enters UTR / Reference No after transfer. Payment remains "Pending Verification" until admin approves against school bank statement.</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="verificationMode" 
                    checked={settings.verificationMode === 'PAYMENT_GATEWAY'} 
                    onChange={() => updateSetting('verificationMode', 'PAYMENT_GATEWAY')} 
                    style={{ marginTop: '3px' }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>○ Payment Gateway (Automated Webhook)</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Automatically confirms successful payments through configured payment gateway webhook (Razorpay / Cashfree).</div>
                  </div>
                </label>
              </div>
            </div>

            {toggleRow('Dynamic QR Code', settings.dynamicQr, v => updateSetting('dynamicQr', v), 'Generate unique QR for each transaction with amount pre-filled')}

            {/* QR Preview */}
            {settings.upiId && (
              <div style={{ marginTop: '20px', padding: '20px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>QR Code Preview</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ width: '150px', height: '150px', background: '#fff', border: '2px solid #e2e8f0', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <QrCode size={60} color="#0f172a" />
                    <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>Auto-generated</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#475569', fontFamily: 'monospace', background: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div>upi://pay?</div>
                    <div>&nbsp;&nbsp;pa={settings.upiId}</div>
                    <div>&nbsp;&nbsp;pn={settings.merchantName || 'School'}</div>
                    <div>&nbsp;&nbsp;cu=INR</div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button style={btnPrimary} onClick={() => saveSettings('UPI')} disabled={saving}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save UPI Settings'}
              </button>
            </div>
          </div>
        )}

        {/* ═══ RECEIPT NUMBERING ═══ */}
        {activeSection === 'receipt' && (
          <div>
            {sectionTitle('Receipt Numbering & Format', <Receipt size={20} color="#2563eb" />)}
            <div style={gridForm}>
              <div>
                <label style={labelStyle}>Receipt Prefix</label>
                <input style={inputStyle} value={settings.receiptPrefix || 'FEE'} onChange={e => updateSetting('receiptPrefix', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Number Padding (digits)</label>
                <input style={inputStyle} type="number" min="3" max="10" value={settings.receiptPadding || 6} onChange={e => updateSetting('receiptPadding', parseInt(e.target.value))} />
              </div>
              <div>
                <label style={labelStyle}>Format Preview</label>
                <div style={{ padding: '10px 14px', background: '#f1f5f9', borderRadius: '8px', fontFamily: 'monospace', fontSize: '14px', fontWeight: '700', color: '#1e40af' }}>
                  {settings.receiptPrefix || 'FEE'}-{new Date().getFullYear()}-{'0'.repeat(Math.max(0, (settings.receiptPadding || 6) - 1))}1
                </div>
              </div>
            </div>
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>Receipt Options</h4>
              {toggleRow('Auto Number', settings.receiptAutoNumber, v => updateSetting('receiptAutoNumber', v), 'Automatically assign sequential receipt numbers')}
              {toggleRow('Auto Print', settings.receiptAutoPrint, v => updateSetting('receiptAutoPrint', v), 'Automatically open print dialog after receipt generation')}
              {toggleRow('Email Receipt', settings.receiptEmailEnabled, v => updateSetting('receiptEmailEnabled', v), 'Send receipt copy via email')}
              {toggleRow('SMS Receipt', settings.receiptSmsEnabled, v => updateSetting('receiptSmsEnabled', v), 'Send receipt notification via SMS')}
              {toggleRow('WhatsApp Receipt', settings.receiptWhatsappEnabled, v => updateSetting('receiptWhatsappEnabled', v), 'Send receipt via WhatsApp')}
              {toggleRow('Digital Signature', settings.receiptDigitalSignature, v => updateSetting('receiptDigitalSignature', v), 'Add digital signature to receipts')}
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button style={btnPrimary} onClick={() => saveSettings('Receipt')} disabled={saving}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save Receipt Settings'}
              </button>
            </div>
          </div>
        )}

        {/* ═══ FEE COLLECTION RULES ═══ */}
        {activeSection === 'rules' && (
          <div>
            {sectionTitle('Fee Collection Rules & Overdue Fines', <Scale size={20} color="#2563eb" />)}
            
            {/* Automatic Overdue Fine Card */}
            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #cbd5e1', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>Automatic Overdue Fine</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Automatically calculate fines on overdue fee installments past grace period</div>
                </div>
                <button type="button" style={toggleStyle(settings.enableAutoOverdueFine)} onClick={() => updateSetting('enableAutoOverdueFine', !settings.enableAutoOverdueFine)}>
                  <span style={toggleKnob(settings.enableAutoOverdueFine)} />
                </button>
              </div>

              {settings.enableAutoOverdueFine && (
                <div style={{ display: 'grid', gap: '16px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <div>
                    <label style={labelStyle}>Fine Type</label>
                    <div style={{ display: 'flex', gap: '20px', marginTop: '6px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#1e293b', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="overdueFineType"
                          value="FIXED"
                          checked={settings.overdueFineType === 'FIXED'}
                          onChange={() => updateSetting('overdueFineType', 'FIXED')}
                        />
                        Fixed Amount (Flat Fine)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#1e293b', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="overdueFineType"
                          value="PER_DAY"
                          checked={settings.overdueFineType !== 'FIXED'}
                          onChange={() => updateSetting('overdueFineType', 'PER_DAY')}
                        />
                        Per Day Overdue
                      </label>
                    </div>
                  </div>

                  <div style={gridForm}>
                    <div>
                      <label style={labelStyle}>Grace Period (Days)</label>
                      <input style={inputStyle} type="number" min="0" value={settings.graceDays ?? 5} onChange={e => updateSetting('graceDays', parseInt(e.target.value) || 0)} />
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>Days after due date before fine starts</span>
                    </div>
                    <div>
                      <label style={labelStyle}>Fine Amount (₹)</label>
                      <input style={inputStyle} type="number" min="0" value={settings.overdueFineAmount ?? 20} onChange={e => updateSetting('overdueFineAmount', parseFloat(e.target.value) || 0)} />
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>{settings.overdueFineType === 'FIXED' ? 'Flat fine per overdue installment' : 'Daily fine per day overdue'}</span>
                    </div>
                    <div>
                      <label style={labelStyle}>Maximum Fine Cap (₹)</label>
                      <input style={inputStyle} type="number" min="0" value={settings.maximumFine ?? 500} onChange={e => updateSetting('maximumFine', parseFloat(e.target.value) || 0)} />
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>Maximum limit fine cannot exceed</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginTop: '20px' }}>
              {toggleRow('Allow Installments', settings.allowInstallments, v => updateSetting('allowInstallments', v), 'Students can pay fees in multiple installments')}
              {toggleRow('Allow Partial Payment', settings.allowPartialPayment, v => updateSetting('allowPartialPayment', v), 'Accept partial amounts against total due')}
              {toggleRow('Allow Advance Payment', settings.allowAdvancePayment, v => updateSetting('allowAdvancePayment', v), 'Accept fees before due date')}
              {toggleRow('Allow Fine Waiver', settings.allowFineWaiver, v => updateSetting('allowFineWaiver', v), 'Admin can waive late fee fines')}
            </div>
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>Offline Payment Methods</h4>
              {toggleRow('Accept Cash', settings.acceptCash, v => updateSetting('acceptCash', v))}
              {toggleRow('Accept Cheque', settings.acceptCheque, v => updateSetting('acceptCheque', v))}
              {toggleRow('Accept Demand Draft', settings.acceptDD, v => updateSetting('acceptDD', v))}
              {toggleRow('Accept Bank Transfer', settings.acceptBankTransfer, v => updateSetting('acceptBankTransfer', v))}
              {settings.acceptCheque && (
                <div style={{ padding: '12px 0' }}>
                  <label style={labelStyle}>Cheque Clearance Days</label>
                  <input style={{ ...inputStyle, maxWidth: '200px' }} type="number" min="1" max="30" value={settings.chequeClearanceDays ?? 3} onChange={e => updateSetting('chequeClearanceDays', parseInt(e.target.value))} />
                </div>
              )}
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button style={btnPrimary} onClick={() => saveSettings('Fee Collection')} disabled={saving}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save Collection Rules'}
              </button>
            </div>
          </div>
        )}

        {/* ═══ NOTIFICATIONS ═══ */}
        {activeSection === 'notifications' && (
          <div>
            {sectionTitle('Payment Notification Settings', <Bell size={20} color="#2563eb" />)}
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>After Payment Notification Channels</h4>
            {toggleRow('Send SMS', settings.notifySmsOnPayment, v => updateSetting('notifySmsOnPayment', v))}
            {toggleRow('Send Email', settings.notifyEmailOnPayment, v => updateSetting('notifyEmailOnPayment', v))}
            {toggleRow('Send WhatsApp', settings.notifyWhatsappOnPayment, v => updateSetting('notifyWhatsappOnPayment', v))}
            {toggleRow('Notify Parent', settings.notifyParent, v => updateSetting('notifyParent', v))}
            {toggleRow('Notify Student', settings.notifyStudent, v => updateSetting('notifyStudent', v))}

            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: '24px 0 10px' }}>Message Templates</h4>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={labelStyle}>SMS Template</label>
                <textarea style={{ ...inputStyle, minHeight: '70px', fontFamily: 'monospace', fontSize: '12px' }} value={settings.smsTemplate || ''} onChange={e => updateSetting('smsTemplate', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Email Template</label>
                <textarea style={{ ...inputStyle, minHeight: '100px', fontFamily: 'monospace', fontSize: '12px' }} value={settings.emailTemplate || ''} onChange={e => updateSetting('emailTemplate', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>WhatsApp Template</label>
                <textarea style={{ ...inputStyle, minHeight: '70px', fontFamily: 'monospace', fontSize: '12px' }} value={settings.whatsappTemplate || ''} onChange={e => updateSetting('whatsappTemplate', e.target.value)} />
              </div>
            </div>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
              Available placeholders: {'{{parentName}}'}, {'{{studentName}}'}, {'{{amount}}'}, {'{{feeType}}'}, {'{{receiptNo}}'}, {'{{date}}'}, {'{{class}}'}, {'{{schoolName}}'}
            </p>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button style={btnPrimary} onClick={() => saveSettings('Notification')} disabled={saving}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save Notification Settings'}
              </button>
            </div>
          </div>
        )}

        {/* ═══ REFUND POLICIES ═══ */}
        {activeSection === 'refund' && (
          <div>
            {sectionTitle('Refund Policies', <RotateCcw size={20} color="#2563eb" />)}
            {toggleRow('Allow Refunds', settings.allowRefund, v => updateSetting('allowRefund', v), 'Enable fee refund processing')}
            {settings.allowRefund && (
              <>
                {toggleRow('Require Approval', settings.refundApprovalRequired, v => updateSetting('refundApprovalRequired', v), 'Refunds require admin or principal approval')}
                {toggleRow('Require Reason', settings.refundReasonRequired, v => updateSetting('refundReasonRequired', v), 'Staff must enter a reason for each refund')}
                {toggleRow('Require Bank Details', settings.refundBankDetailsRequired, v => updateSetting('refundBankDetailsRequired', v), 'Collect parent bank details for refund')}
                <div style={{ padding: '12px 0' }}>
                  <label style={labelStyle}>Maximum Refund Limit (₹)</label>
                  <input style={{ ...inputStyle, maxWidth: '250px' }} type="number" min="0" value={settings.maximumRefundLimit ?? 50000} onChange={e => updateSetting('maximumRefundLimit', parseFloat(e.target.value))} />
                </div>
              </>
            )}
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button style={btnPrimary} onClick={() => saveSettings('Refund')} disabled={saving}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save Refund Settings'}
              </button>
            </div>
          </div>
        )}

        {/* ═══ SECURITY ═══ */}
        {activeSection === 'security' && (
          <div>
            {sectionTitle('Payment Security Settings', <ShieldCheck size={20} color="#2563eb" />)}
            {toggleRow('Require Admin PIN', settings.requireAdminPin, v => updateSetting('requireAdminPin', v), 'Require PIN before processing payment')}
            {toggleRow('Require OTP Verification', settings.requireOtp, v => updateSetting('requireOtp', v), 'Send OTP to admin before high-value transactions')}
            {toggleRow('Webhook Signature Validation', settings.webhookValidation, v => updateSetting('webhookValidation', v), 'Validate webhook signatures from payment gateways')}
            {toggleRow('Duplicate Transaction Detection', settings.duplicateTransactionCheck, v => updateSetting('duplicateTransactionCheck', v), 'Prevent duplicate payment processing')}
            {toggleRow('IP Restriction', settings.ipRestriction, v => updateSetting('ipRestriction', v), 'Restrict fee collection to whitelisted IPs')}
            {toggleRow('Audit Logging', settings.auditLogEnabled, v => updateSetting('auditLogEnabled', v), 'Log all payment actions for compliance')}
            <div style={{ padding: '12px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Maximum Discount Limit (₹)</label>
                <input style={inputStyle} type="number" min="0" value={settings.maximumDiscountLimit ?? 10000} onChange={e => updateSetting('maximumDiscountLimit', parseFloat(e.target.value))} />
              </div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button style={btnPrimary} onClick={() => saveSettings('Security')} disabled={saving}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save Security Settings'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══ BANK ACCOUNT MODAL ═══ */}
      {showBankModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={e => e.target === e.currentTarget && setShowBankModal(false)}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '560px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '20px' }}>
              {editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}
            </h3>
            <div style={gridForm}>
              <div>
                <label style={labelStyle}>Account Name *</label>
                <input style={inputStyle} value={bankForm.accountName} onChange={e => setBankForm({ ...bankForm, accountName: e.target.value })} placeholder="General Fee Account" />
              </div>
              <div>
                <label style={labelStyle}>Purpose</label>
                <select style={inputStyle} value={bankForm.purpose} onChange={e => setBankForm({ ...bankForm, purpose: e.target.value })}>
                  {PURPOSES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Account Holder Name</label>
                <input style={inputStyle} value={bankForm.accountHolderName} onChange={e => setBankForm({ ...bankForm, accountHolderName: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Bank Name</label>
                <input style={inputStyle} value={bankForm.bankName} onChange={e => setBankForm({ ...bankForm, bankName: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Branch Name</label>
                <input style={inputStyle} value={bankForm.branchName} onChange={e => setBankForm({ ...bankForm, branchName: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Account Number</label>
                <input style={inputStyle} value={bankForm.accountNumber} onChange={e => setBankForm({ ...bankForm, accountNumber: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>IFSC Code</label>
                <input style={inputStyle} value={bankForm.ifscCode} onChange={e => setBankForm({ ...bankForm, ifscCode: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>UPI ID</label>
                <input style={inputStyle} value={bankForm.upiId} onChange={e => setBankForm({ ...bankForm, upiId: e.target.value })} placeholder="school@upi" />
              </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={bankForm.isDefault} onChange={e => setBankForm({ ...bankForm, isDefault: e.target.checked })} /> Set as Default Account
              </label>
            </div>
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button style={btnOutline} onClick={() => setShowBankModal(false)}>Cancel</button>
              <button style={btnPrimary} onClick={saveBankAccount} disabled={saving || !bankForm.accountName}>
                <Save size={14} /> {saving ? 'Saving...' : editingAccount ? 'Update' : 'Add Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════ GATEWAY FORM SUB-COMPONENT ═══════════════
const GatewayForm = ({ gateway, onSave, saving }) => {
  const [form, setForm] = useState({ ...gateway });

  return (
    <div>
      {toggleRow('Enable Gateway', form.enabled, v => setForm({ ...form, enabled: v }))}
      {form.enabled && (
        <div style={{ ...gridForm, marginTop: '12px' }}>
          <div>
            <label style={labelStyle}>Merchant ID</label>
            <input style={inputStyle} value={form.merchantId || ''} onChange={e => setForm({ ...form, merchantId: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Key ID</label>
            <input style={inputStyle} value={form.keyId || ''} onChange={e => setForm({ ...form, keyId: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Secret Key</label>
            <input style={inputStyle} type="password" value={form.secretKey || ''} onChange={e => setForm({ ...form, secretKey: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Webhook Secret</label>
            <input style={inputStyle} value={form.webhookSecret || ''} onChange={e => setForm({ ...form, webhookSecret: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Environment</label>
            <select style={inputStyle} value={form.environment || 'SANDBOX'} onChange={e => setForm({ ...form, environment: e.target.value })}>
              <option value="SANDBOX">🧪 Sandbox (Testing)</option>
              <option value="PRODUCTION">🔴 Production (Live)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Transaction Fee (%)</label>
            <input style={inputStyle} type="number" step="0.01" min="0" value={form.transactionFeePercent ?? ''} onChange={e => setForm({ ...form, transactionFeePercent: parseFloat(e.target.value) })} placeholder="e.g. 2.0" />
          </div>
        </div>
      )}
      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
        <button style={btnPrimary} onClick={() => onSave(form)} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

export default PaymentBankSettings;
